// Role guards. Lane A scaffold; fully wired up in Lane B alongside the
// store_manager role expansion.

export type Role = 'admin' | 'store_manager' | 'staff';
export type Store = 'NY' | 'DT' | 'RH' | 'YF' | 'ALL';

export interface User {
  id: string;
  name: string;
  role: Role | 'employee';
  stores: Store[];
  canManageUsers?: boolean;
  superAdmin?: boolean;            // 超级管理员 (Regina) — HR restricted-profile access
  shift?: 'morning' | 'evening' | null;  // 领班's led shift; truthy = 领班
  csManager?: boolean;            // 客服管理员: scoped manager of 客服-dept staff accounts
}

export interface StaffLike {
  id: string;
  stores: Store[];
}

export function isAdmin(user: User | null | undefined): boolean {
  return !!user && user.role === 'admin';
}

export function isStoreManager(user: User | null | undefined): boolean {
  return !!user && user.role === 'store_manager';
}

// 领班 (shift lead): a staff account with a shift assigned. A capability, not a
// role — stays narrow everywhere except 人事档案.
export function isShiftLead(user: User | null | undefined): boolean {
  return !!user && !!user.shift;
}

// 人事档案 access tab gate: admin, 店长, or 领班.
export function canAccessHR(user: User | null | undefined): boolean {
  return isElevated(user) || isShiftLead(user);
}

// Can the actor SEE salary in 人事档案? 管理员 (all) or 领班 (their store+shift).
export function canSeeHrSalary(user: User | null | undefined): boolean {
  return !!user && (user.role === 'admin' || isShiftLead(user));
}

// "Elevated" = admin OR 店长. Per Regina's 2026-05-26 decision: store managers
// run daily operations and share the operational permission set with admin
// (delete tasks/inventory events, user management, etc.). Admin-protect lives
// in the individual routes that touch admin accounts.
export function isElevated(user: User | null | undefined): boolean {
  return !!user && (user.role === 'admin' || user.role === 'store_manager');
}

// 客服管理员: scoped user-manager for the 客服 department. A capability, not a role.
export function isCsManager(user: User | null | undefined): boolean {
  return !!user && !!user.csManager;
}

// User-management tab gate. Mirrors requireUserMgmt: 超级管理员 (full) or 客服管理员
// (scoped server-side to 客服-dept staff). Legacy elevated/flag holders retained
// so existing behavior is unchanged.
export function canManageUsers(user: User | null | undefined): boolean {
  return !!user && (isElevated(user) || !!user.canManageUsers || !!user.superAdmin || isCsManager(user));
}

export function canViewPerson(viewer: User, targetId: string): boolean {
  if (isAdmin(viewer)) return true;
  if (viewer.id === targetId) return true;
  return false;
}

export function canEnterExamScore(actor: User, _targetStaff: StaffLike): boolean {
  // Admin and 店长 can enter exam scores for anyone, regardless of store.
  // Per Regina's 2026-05-26 call: store managers run daily operations across
  // the chain, not just their own location.
  return isElevated(actor);
}
